import { useCallback, useState } from 'react';

import {
  useLoggedInEmployeeProfile,
  useLoggedInUserPermissions,
} from '~/components/Auth';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { GET_TIMESHEET_SWR_KEY, getTimeSheet } from '~/data/worktime';
import { useSWR } from '~/hooks/useSWR';
import {
  AvailableBalances,
  useVacationBalances,
} from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/useVacationBalances';
import { People } from '~/services/Http';
import { AppModules } from '~/services/Permissions';

export interface UseMyDataWidgetContentOutput {
  loading: boolean;
  failedToLoad: boolean;
  myEmployeeInfo: People.EmployeeProfileDto;
  timeReportingIsEnabled: boolean;
  selectedEmployeeId?: number;
  openEmployeeProfileDialog: (employeeId: number) => void;
  closeEmployeeProfileDialog: () => void;
  extraHoursBalance?: string;
  availableBalances: AvailableBalances;
  vacationBalance?: number;
}

export const useMyDataWidgetContent = (): UseMyDataWidgetContentOutput => {
  const myEmployeeInfo = useLoggedInEmployeeProfile();
  const permissions = useLoggedInUserPermissions();

  const timeReportingIsEnabled = permissions.enabledModules.includes(
    AppModules.WORK_TIME_REPORTING,
  );

  const {
    data: timeSheet,
    loading: timeSheetLoading,
    failedToLoad: timeSheetFailedToLoad,
  } = useSWR(
    timeReportingIsEnabled ? [GET_TIMESHEET_SWR_KEY, myEmployeeInfo.id] : null,
    ([_, employeeId]) => getTimeSheet(employeeId),
  );

  const {
    absencesData: absence,
    loading: absenceLoading,
    failedToLoad: absenceFailedToLoad,
  } = useEmployeeAbsenceData({ employeeId: myEmployeeInfo.id });

  const {
    availableBalances,
    isLoading: isLoadingBalances,
    failedToLoad: failedToLoadBalances,
  } = useVacationBalances({ absence });

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>();

  const openEmployeeProfileDialog = useCallback(
    (employeeId: number) => {
      setSelectedEmployeeId(employeeId);
    },
    [setSelectedEmployeeId],
  );

  const closeEmployeeProfileDialog = useCallback(() => {
    setSelectedEmployeeId(undefined);
  }, [setSelectedEmployeeId]);

  return {
    loading:
      (timeReportingIsEnabled && timeSheetLoading) ||
      absenceLoading ||
      isLoadingBalances,
    failedToLoad:
      (timeReportingIsEnabled && timeSheetFailedToLoad) ||
      absenceFailedToLoad ||
      failedToLoadBalances,
    myEmployeeInfo,
    timeReportingIsEnabled,
    selectedEmployeeId,
    openEmployeeProfileDialog,
    closeEmployeeProfileDialog,
    extraHoursBalance: timeSheet?.extraHoursBalance,
    availableBalances,
    vacationBalance:
      absence && absence.vacationBalance
        ? absence?.vacationBalance.fVacationBalance -
          absence?.vacationBalance.fVacationBalanceProjDed.fProjectedApproved -
          absence?.vacationBalance.fVacationBalanceProjDed.fProjectedUnapproved
        : 0,
  };
};
